import {useEffect, useRef, useState} from 'react';
import type {WyscoutPlayerFixtureDetail} from './wyscoutFixtureApi';

const completionMessage = (detail: WyscoutPlayerFixtureDetail): string => {
  const eventsFetched = detail.eventStatus === 'fetched';
  const matchFetched = detail.matchCacheState === 'fetched';
  if (eventsFetched && matchFetched) return 'Match details and events refreshed.';
  if (eventsFetched) return 'Match events refreshed.';
  if (matchFetched) return 'Match details refreshed.';
  return '';
};

export const useWyscoutFixtureRefreshAnnouncement = (
  identity: string | null,
  fetchStatus: 'fetching' | 'paused' | 'idle',
  isError: boolean,
  dataUpdatedAt: number,
  detail: WyscoutPlayerFixtureDetail | undefined,
): string => {
  const [announcement, setAnnouncement] = useState('');
  const observedFetch = useRef<{identity: string; dataUpdatedAt: number} | null>(null);
  const activeIdentity = useRef<string | null>(null);

  useEffect(() => {
    if (activeIdentity.current !== identity) {
      activeIdentity.current = identity;
      observedFetch.current = null;
      setAnnouncement('');
    }
    if (!identity) {
      return;
    }
    if (fetchStatus === 'fetching') {
      observedFetch.current = {identity, dataUpdatedAt};
      setAnnouncement('');
      return;
    }
    const observed = observedFetch.current;
    if (fetchStatus !== 'idle' || observed?.identity !== identity) return;
    observedFetch.current = null;
    if (isError || !detail || dataUpdatedAt <= observed.dataUpdatedAt) return;
    setAnnouncement(completionMessage(detail));
  }, [dataUpdatedAt, detail, fetchStatus, identity, isError]);

  return announcement;
};
